import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import emailjs from '@emailjs/browser';
import Footer from './Footer';
import { useTheme } from '../context/ThemeContext';
import {
  Mail,
  Phone,
  MapPin,
  Send,
  User,
  MessageSquare,
  ArrowLeft
} from 'lucide-react';
import { db, collection, addDoc, serverTimestamp } from './firebase';
import BackButton from './BackButton';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { cn } from '../lib/utils';

const ContactUS = () => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState({ name: '', phone: '', message: '', email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields.', { position: 'top-right' });
      setIsSubmitting(false);
      return;
    }
    try {
      const contactData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || 'Not provided',
        message: formData.message,
        timestamp: serverTimestamp(),
        status: 'new'
      };

      await addDoc(collection(db, 'contactResponses'), contactData);

      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone || 'Not provided',
        message: formData.message,
        to_name: 'SnapED Admin',
        reply_to: formData.email,
        submitted_at: new Date().toLocaleString('en-IN', {
          dateStyle: 'full',
          timeStyle: 'short'
        })
      };

      try {
        const result = await emailjs.send(
          process.env.REACT_APP_EMAILJS_SERVICE_ID,
          process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
          templateParams,
          process.env.REACT_APP_EMAILJS_PUBLIC_KEY
        );

        if (result.status === 200) {
          toast.success('Message sent successfully! We will get back to you soon.', { position: 'top-right' });
        }
      } catch (emailError) {
        console.error('EmailJS Error:', emailError);
        toast.info('Message received! (Notification pending)', { position: 'top-right' });
      }

      setFormData({ name: '', phone: '', message: '', email: '' });
    } catch (error) {
      console.error('Firestore Error:', error);
      toast.error('Failed to send message. Please check your connection and try again.', { position: 'top-right' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-background min-h-screen transition-colors duration-300">
      <div className="fixed top-6 left-6 z-40 text-foreground">
        <BackButton to="/home" label="Back to Home" />
      </div>

      <section className="container max-w-7xl mx-auto px-6 pt-32 pb-24">
        <ToastContainer theme={isDarkMode ? "dark" : "light"} />

        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <Badge variant="outline" className="px-4 py-1.5 rounded-full bg-primary/10 border-primary/20 text-primary font-bold text-[10px] uppercase tracking-[0.2em] mb-6 shadow-sm">
            <Mail className="mr-2 h-3.5 w-3.5" /> Contact Us
          </Badge>
          <h1 className="text-5xl md:text-7xl font-black text-foreground tracking-tighter leading-[1.1] mb-6">
            Get in <span className="text-primary">Touch</span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-xl mx-auto font-medium leading-relaxed">
            Our team is here to help you. Send us a message and we'll respond as soon as possible.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-[400px,1fr] gap-12 relative items-start">
          {/* Background Glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4/5 h-4/5 bg-primary/5 blur-[120px] rounded-full pointer-events-none -z-10" />

          {/* Contact Details Card */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="bg-card/50 backdrop-blur-2xl border-border/60 p-8 md:p-10 rounded-[2.5rem] shadow-2xl space-y-10">
              <div className="space-y-2">
                <h2 className="text-2xl font-black text-foreground tracking-tight">Contact Information</h2>
                <p className="text-muted-foreground text-sm font-medium">Find the best way to reach us.</p>
              </div>

              <div className="space-y-8">
                {[
                  { icon: <Mail className="h-5 w-5" />, title: 'Email Address', value: 'snaped4338@gmail.com', sub: '24/7 Support' },
                  { icon: <Phone className="h-5 w-5" />, title: 'Phone Number', value: '+91 98713 91381', sub: 'Mon-Fri 9am-6pm' },
                  { icon: <MapPin className="h-5 w-5" />, title: 'Our Office', value: 'Delhi, India', sub: 'India' }
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-5 items-center group">
                    <div className="w-14 h-14 rounded-2xl bg-secondary/50 border border-border flex items-center justify-center text-primary group-hover:scale-110 transition-transform shadow-inner">
                      {item.icon}
                    </div>
                    <div>
                      <div className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-1">{item.title}</div>
                      <div className="text-foreground font-extrabold text-base tracking-tight">{item.value}</div>
                      <div className="text-xs font-bold text-primary mt-0.5">{item.sub}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-6 rounded-3xl bg-primary/[0.03] border border-primary/10">
                <p className="text-muted-foreground text-xs leading-relaxed font-medium">
                  Need immediate help? Check our FAQ section or drop a line here. We typically respond within 12 hours.
                </p>
              </div>
            </Card>
          </motion.div>

          {/* Form Card */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="bg-card/50 backdrop-blur-2xl border-border/60 p-8 md:p-12 rounded-[2.5rem] shadow-2xl">
              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-3">
                    <label className="text-[11px] font-black text-muted-foreground uppercase tracking-[0.15em] ml-1">Full Name</label>
                    <div className="relative group">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-primary transition-colors group-focus-within:text-primary" />
                      <Input
                        name="name"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="pl-12 h-14 rounded-2xl border-border/80 bg-background/50 focus:ring-primary/20 transition-all font-medium text-sm"
                      />
                    </div>
                  </div>
                  <div className="space-y-3">
                    <label className="text-[11px] font-black text-muted-foreground uppercase tracking-[0.15em] ml-1">Phone (Optional)</label>
                    <div className="relative group">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-primary" />
                      <Input
                        name="phone"
                        placeholder="+91 XXXX"
                        value={formData.phone}
                        onChange={handleChange}
                        className="pl-12 h-14 rounded-2xl border-border/80 bg-background/50 focus:ring-primary/20 transition-all font-medium text-sm"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-[11px] font-black text-muted-foreground uppercase tracking-[0.15em] ml-1">Email Address</label>
                  <div className="relative group">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-primary" />
                    <Input
                      type="email"
                      name="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="pl-12 h-14 rounded-2xl border-border/80 bg-background/50 focus:ring-primary/20 transition-all font-medium text-sm"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-[11px] font-black text-muted-foreground uppercase tracking-[0.15em] ml-1">Your Message</label>
                  <div className="relative group">
                    <MessageSquare className="absolute left-4 top-5 h-4 w-4 text-primary" />
                    <Textarea
                      name="message"
                      placeholder="How can we help you?"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      className="pl-12 pt-4 min-h-[160px] rounded-2xl border-border/80 bg-background/50 focus:ring-primary/20 transition-all font-medium text-sm resize-none"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full h-16 rounded-2xl bg-primary hover:bg-primary/90 text-primary-foreground font-black text-base shadow-xl shadow-primary/20 transition-all hover:scale-[1.02] active:scale-[0.98] mt-4"
                >
                  {isSubmitting ? (
                    'Sending Message...'
                  ) : (
                    <>
                      <Send className="mr-2 h-5 w-5" /> Send Message
                    </>
                  )}
                </Button>
              </form>
            </Card>
          </motion.div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default ContactUS;
